import React, { useEffect, useRef, useState } from "react";
import { X, Link2, Search, ChevronDown, Building2 } from "lucide-react";
import { toast } from "sonner";
import useRequest from "../../../components/hook/use-req";
import { UnconnectedOrg, UnconnectedOrgsResponse } from "./types";

// Searchable business picker — GET /admin/resellers/{id}/organizations/unconnected?search=.
// Shows each business's name, but tracks the selected org's id underneath;
// that id (not the typed text) is what gets sent as org_id.
const OrgPicker: React.FC<{
  resellerId: string;
  selected: UnconnectedOrg | null;
  onSelect: (org: UnconnectedOrg | null) => void;
}> = ({ resellerId, selected, onSelect }) => {
  const token = localStorage.getItem("authToken");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [orgs, setOrgs] = useState<UnconnectedOrg[]>([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { makeRequest: getUnconnectedOrgs } = useRequest(
    `/admin/resellers/${resellerId}/organizations/unconnected`,
    "GET",
    { Authorization: `Bearer ${token}` }
  );

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedQuery(query.trim()), 350);
    return () => clearTimeout(handler);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const fetchOrgs = async () => {
      setLoading(true);
      const params: Record<string, unknown> = { page: 1, limit: 20 };
      if (debouncedQuery) params.search = debouncedQuery;
      const [response] = (await getUnconnectedOrgs(undefined, params)) as [UnconnectedOrgsResponse, number];
      setOrgs(response?.status ? response.organizations : []);
      setLoading(false);
    };
    fetchOrgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debouncedQuery, resellerId]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePick = (org: UnconnectedOrg) => {
    onSelect(org);
    setQuery("");
    setOpen(false);
  };

  const handleTyping = (value: string) => {
    setQuery(value);
    // Typing again after having picked one clears the selection — the input
    // is either showing a committed pick or a live search, never both.
    if (selected) onSelect(null);
    if (!open) setOpen(true);
  };

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
        <input
          value={selected ? selected.name : query}
          onChange={(e) => handleTyping(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="Search businesses by name…"
          className="w-full pl-9 pr-8 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <ChevronDown
          size={14}
          className={`absolute right-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </div>

      {open && (
        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
          {loading ? (
            <div className="px-3 py-3 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 text-center">Searching…</div>
          ) : orgs.length === 0 ? (
            <div className="px-3 py-3 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 text-center">
              {debouncedQuery ? "No businesses match your search." : "No unattached businesses found."}
            </div>
          ) : (
            orgs.map((org) => (
              <button
                key={org.id}
                type="button"
                onClick={() => handlePick(org)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
              >
                <span className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-500/15 flex items-center justify-center shrink-0">
                  <Building2 size={12} className="text-blue-600 dark:text-blue-400" />
                </span>
                <span className="min-w-0 flex-1 truncate">{org.name}</span>
                {!org.is_active && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 shrink-0">
                    inactive
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// POST /admin/resellers/{id}/organizations — { org_id }. Sets
// organizations.parent_org_id to the reseller's own organization_id.
const AttachOrgModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  resellerId: string;
  onAttached: () => void;
}> = ({ isOpen, onClose, resellerId, onAttached }) => {
  const token = localStorage.getItem("authToken");
  const [selectedOrg, setSelectedOrg] = useState<UnconnectedOrg | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { makeRequest: attachOrg } = useRequest(`/admin/resellers/${resellerId}/organizations`, "POST", {
    Authorization: `Bearer ${token}`,
  });

  const handleClose = () => {
    if (submitting) return;
    setSelectedOrg(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedOrg) {
      toast.error("Pick a business to attach.");
      return;
    }
    setSubmitting(true);
    const [response, status] = await attachOrg({ org_id: selectedOrg.id });
    setSubmitting(false);
    if (status >= 200 && status < 300 && response?.status !== false) {
      toast.success(response?.message || `${selectedOrg.name} attached.`);
      setSelectedOrg(null);
      onAttached();
      onClose();
    } else {
      toast.error(response?.detail || response?.message || "Failed to attach organization.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-500/15 flex items-center justify-center shrink-0">
              <Link2 size={16} className="text-blue-600 dark:text-blue-400" />
            </span>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Attach Organization</h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 rounded-lg text-[#7B7B7B] font-semibold hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Business</label>
            <OrgPicker resellerId={resellerId} selected={selectedOrg} onSelect={setSelectedOrg} />
          </div>
          <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">
            Only businesses not already attached to a reseller show up here.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2.5 px-5 py-4 border-t border-gray-100 dark:border-gray-800">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !selectedOrg}
            className="px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-sm transition-colors"
          >
            {submitting ? "Attaching…" : "Attach"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttachOrgModal;
